// src/services/CryptoService.ts

import CryptoUtilities from './CryptoUtilities';

/**
 * Classe per gestire le operazioni di crittografia e decrittografia.
 */
export class CryptoService {
  private static instance: CryptoService;
  private crypto: Crypto;

  private constructor() {
    if (typeof window !== 'undefined' && window.crypto) {
      this.crypto = window.crypto;
    } else if (typeof globalThis !== 'undefined' && globalThis.crypto) {
      this.crypto = globalThis.crypto;
    } else {
      throw new Error('No cryptographic support available');
    }
  }

  /**
   * Ottiene l'istanza unica di CryptoService.
   * @returns L'istanza di CryptoService.
   */
  public static getInstance(): CryptoService {
    if (!CryptoService.instance) {
      CryptoService.instance = new CryptoService();
    }
    return CryptoService.instance;
  }

  /**
   * Genera un vettore di inizializzazione (IV) casuale.
   * @returns Una ArrayBuffer che rappresenta l'IV.
   */
  public async generateIV(): Promise<ArrayBuffer> {
    const iv = await this.crypto.getRandomValues(new Uint8Array(16));
    return iv.buffer;
  }

  public async generateDek(): Promise<ArrayBuffer> {
    const dek = await this.crypto.getRandomValues(new Uint8Array(32));
    return dek.buffer;
  }

  public async generateMK(): Promise<ArrayBuffer> {
    const mk = await this.crypto.getRandomValues(new Uint8Array(32));
    return mk.buffer;
  }

  public async generateEzKey(): Promise<ArrayBuffer> {
    const ezKey = await this.crypto.getRandomValues(new Uint8Array(32));
    return ezKey.buffer;
  }

  public async generateRandomValues(length: number): Promise<Uint8Array> {
    const randomValues = await this.crypto.getRandomValues(new Uint8Array(length));
    return randomValues;
  }
  /**
   * Genera una chiave crittografica AES.
   * @returns Una stringa che rappresenta la chiave in formato base64.
   */
  public async generateKey(): Promise<ArrayBuffer> {
    const key = await this.crypto.subtle.generateKey(
      { name: 'AES-CBC', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
    const exportedKey = await this.crypto.subtle.exportKey('raw', key);
    return exportedKey;
  }

  /**
   * Crittografa i dati utilizzando AES in modalità CBC.
   * @param key La chiave.
   * @param b64data I dati da crittografare.
   * @param b64iv Il vettore di inizializzazione.
   * @returns I dati crittografati con l'IV prepeso.
   */
  public async encryptCBC(
    key: ArrayBuffer,
    data: ArrayBuffer,
    iv: ArrayBuffer
  ): Promise<ArrayBuffer> {
    const cryptoKey = await this.importKey(key);
    const encryptedData = await this.crypto.subtle.encrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      data
    );
    return CryptoUtilities.concatArrayBuffer(iv, encryptedData); // Prepend IV to encrypted data
  }

  /**
   * Decrittografa i dati crittografati utilizzando AES in modalità CBC.
   * @param key La chiave.
   * @param encryptedData I dati crittografati.
   * @returns I dati decrittografati.
   */
  public async decryptCBC(
    key: ArrayBuffer,
    encryptedData: ArrayBuffer
  ): Promise<ArrayBuffer> {
    //debugger
    const cryptoKey = await this.importKey(key);
    const iv = encryptedData.slice(0, 16); // First 16 bytes are IV
    const data = encryptedData.slice(16); // Rest is encrypted data
    const decryptedData = await this.crypto.subtle.decrypt(
      { name: 'AES-CBC', iv },
      cryptoKey,
      data
    );
    return decryptedData;
  }

  /**
   * Calcola l'hash SHA-256 dei dati forniti.
   * @param data I dati da hashare.
   * @returns L'hash.
   */
  public async hashSHA256(data: string | ArrayBuffer): Promise<ArrayBuffer> {
    const dataBuffer: ArrayBuffer =
      typeof data === 'string'
        ? CryptoUtilities.UTF8StringToArrayBuffer(data)
        : data;
    const hashBuffer = await this.crypto.subtle.digest('SHA-256', dataBuffer);
    return hashBuffer;
  }

  /**
   * Importa una chiave da una stringa in formato base64.
   * @param key La chiave in formato base64.
   * @returns La chiave crittografica importata.
   */
  public async importKey(key: ArrayBuffer): Promise<CryptoKey> {
    return this.crypto.subtle.importKey(
      'raw',
      key,
      { name: 'AES-CBC' },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Deriva una chiave utilizzando PBKDF2.
   * @param passphrase La passphrase da utilizzare.
   * @param salt Il sale da utilizzare per la derivazione.
   * @param iterations Il numero di iterazioni (default: 100000).
   * @param keyLength La lunghezza della chiave in bit (default: 256).
   * @returns La chiave derivata.
   */
  public async pbkdf2(
    password: string,
    salt: ArrayBuffer,
    iterations: number = 1000,
    keyLength: number = 256
  ): Promise<ArrayBuffer> {
    const key = await crypto.subtle.importKey(
      'raw',
      CryptoUtilities.UTF8StringToArrayBuffer(password),
      { name: 'PBKDF2' },
      false,
      ['deriveBits']
    );
    return crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: iterations,
        hash: 'SHA-256',
      },
      key,
      keyLength
    );
  }

  /**
   * Calcola un MAC (Message Authentication Code) utilizzando una chiave MAC e i dati.
   * @param macKey - La chiave MAC da utilizzare.
   * @param data - I dati per cui calcolare il MAC.
   * @returns Il MAC calcolato.
   */
  public async calculateMac(
    macKey: ArrayBuffer,
    data: ArrayBuffer
  ): Promise<ArrayBuffer> {
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      macKey,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    return crypto.subtle.sign('HMAC', cryptoKey, data);
  }

  public async getPbkdf2KeyDerivation(
    key: string,
    salt: ArrayBuffer
  ): Promise<ArrayBuffer> {
    // Calcola l'hash SHA-256 del salt
    const hashedSalt = await this.hashSHA256(salt);

    // Deriva la chiave usando PBKDF2
    const derivedKey = await this.pbkdf2(key, hashedSalt, 1000, 256);

    return derivedKey;
  }
}

export default CryptoService;
